export const QUALITY_PRESETS = {
    low: {
        tier: 'low',
        maxResolution: 1,
        artScale: 1,
        lightmap: false,
        lightmapScale: 0.25,
        minZoom: 0.7,
        minResolutionScale: 0.75,
        pathBudgetPerTick: 4,
    },
    med: {
        tier: 'med',
        maxResolution: 1.5,
        artScale: 2,
        lightmap: true,
        lightmapScale: 0.25,
        minZoom: 0.6,
        minResolutionScale: 0.67,
        pathBudgetPerTick: 6,
    },
    high: {
        tier: 'high',
        maxResolution: 2,
        artScale: 2,
        lightmap: true,
        lightmapScale: 0.5,
        minZoom: 0.5,
        minResolutionScale: 0.6,
        pathBudgetPerTick: 8,
    },
};
/** GPUs that we treat as low tier regardless of other signals. */
const LOW_GPU = /(Mali-(4\d\d|T[67]\d\d|G31|G51|G52))|(PowerVR (SGX|Rogue GE8\d\d\d))|(Adreno \(TM\) (3\d\d|4\d\d|50\d))|SwiftShader|llvmpipe|Software/i;
/** Simple, explainable heuristic. The runtime frame-time watchdog corrects mistakes. */
export function detectTier(info) {
    const reasons = [];
    if (info.gpu && LOW_GPU.test(info.gpu)) {
        reasons.push(`low-end GPU "${info.gpu}"`);
        return { tier: 'low', reasons };
    }
    if (info.memoryGB !== undefined && info.memoryGB <= 2) {
        reasons.push(`deviceMemory ${info.memoryGB} GB <= 2`);
        return { tier: 'low', reasons };
    }
    if (info.isMobile) {
        if (info.cores <= 4) {
            reasons.push(`mobile with ${info.cores} cores`);
            return { tier: 'low', reasons };
        }
        if (info.memoryGB !== undefined && info.memoryGB >= 6 && info.cores >= 8) {
            reasons.push(`mobile with ${info.memoryGB} GB and ${info.cores} cores`);
            return { tier: 'high', reasons };
        }
        reasons.push('mobile default');
        return { tier: 'med', reasons };
    }
    if (info.cores <= 2) {
        reasons.push(`desktop with ${info.cores} cores`);
        return { tier: 'low', reasons };
    }
    reasons.push('desktop default');
    return { tier: 'high', reasons };
}
/** Resolution actually used by the renderer: min(DPR, tier cap) * dynamic scale, never above 2. */
export function effectiveResolution(dpr, settings, dynamicScale) {
    const base = Math.min(dpr, settings.maxResolution, 2);
    return Math.max(0.5, base * dynamicScale);
}
/**
 * Dynamic-resolution watchdog. Feed it frame times; it lowers the resolution
 * scale in steps when the frame budget is missed for `windowMs`, and raises it
 * again (slowly) when there is plenty of headroom.
 */
export class FrameBudgetWatchdog {
    budgetMs;
    minScale;
    windowMs;
    scale = 1;
    sum = 0;
    frames = 0;
    elapsed = 0;
    constructor(budgetMs, minScale, windowMs = 3000) {
        this.budgetMs = budgetMs;
        this.minScale = minScale;
        this.windowMs = windowMs;
    }
    /** Returns true when `scale` changed. */
    push(frameMs) {
        this.sum += frameMs;
        this.frames++;
        this.elapsed += frameMs;
        if (this.elapsed < this.windowMs)
            return false;
        const avg = this.sum / this.frames;
        this.sum = 0;
        this.frames = 0;
        this.elapsed = 0;
        const old = this.scale;
        if (avg > this.budgetMs * 1.15 && this.scale > this.minScale) {
            this.scale = Math.max(this.minScale, Math.round((this.scale - 0.1) * 100) / 100);
        }
        else if (avg < this.budgetMs * 0.6 && this.scale < 1) {
            this.scale = Math.min(1, Math.round((this.scale + 0.05) * 100) / 100);
        }
        return this.scale !== old;
    }
}
